/**
 * Who owns a keystroke while a viewer is open.
 *
 * **Both viewers bind their shortcuts on `document`, and that is the right
 * seam** — a reader is a full-screen surface, and requiring focus inside it
 * before `+` zooms would mean the keys go dead the moment somebody clicks the
 * page they are reading. The cost is that the listener sees every keystroke on
 * the screen, including the ones meant for a text field.
 *
 * That cost was being paid: `PdfViewer` claimed `+`, `=`, `-` and `0` for zoom
 * and called `preventDefault()` unconditionally, which on `keydown` suppresses
 * the character insertion itself. A signer typing a date into a signing field
 * (`01-02-2026`) got `1223`. The page-number box could not be typed past 9.
 *
 * So: shortcuts belong to the viewer *except* when somebody is typing, and
 * except when the platform's own modifier is held — `Ctrl`/`Cmd` with `+`, `-`
 * or `0` is the browser's page zoom, and stealing it is worse than not offering
 * ours.
 */

/**
 * True when the keystroke is destined for a text entry, and a viewer shortcut
 * would be taking it out of somebody's hands.
 *
 * `select` is here with the text controls because a `<select>` reads its own
 * printable keys to jump between options.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;

  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * True when a viewer should leave a `keydown` entirely alone.
 *
 * The one call every document-level viewer shortcut makes before it does
 * anything, so the answer cannot drift between two readers that look the same.
 */
export function isForeignKey(event: KeyboardEvent): boolean {
  return event.ctrlKey || event.metaKey || isTypingTarget(event.target);
}
